import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import useSEO from "@/lib/useSEO";
import Marquee from "@/components/Marquee";

const SECTIONS = [
  {
    n: "01",
    title: "Why a shirt?",
    body: "Because a yard sign gets stolen, a tweet gets buried, and a bumper sticker peels. But a good tee or hoodie gets worn to the grocery store, the school pickup line, and Thanksgiving dinner — over and over, for years. It's the longest-lasting argument you'll ever make without saying a word.",
  },
  {
    n: "02",
    title: "Why funny?",
    body: "Because fury is exhausting and nobody forwards it. Humor is the delivery system that gets past the defenses. Every design here punches up at the government and fascists, never down at people — and aims to be the funniest thing in the room, not the loudest.",
  },
  {
    n: "03",
    title: "Why the specs?",
    body: "Because a flimsy shirt with a bold message is a contradiction — and \"heavyweight\" shouldn't be a blanket claim. Unisex tees: genuine Comfort Colors 1717, 6.1 oz garment-dyed cotton. Women's relaxed tees: Next Level NL3910, 4.3 oz combed ring-spun cotton. Fitted baby tees: Bella+Canvas 1010, 5.3 oz micro-rib. Hoodies: Lane Seven LS19001, 340 gsm heavyweight fleece. The exact blank, published on every page. If we're going to say something loud, the fabric better back it up.",
  },
  {
    n: "04",
    title: "Where does the money go?",
    body: "The price covers the garment, printing, shipping, payment fees, and running the store. We give 5% of annual net profits to causes such as RAICES, which provides free and low-cost legal defense to immigrant and refugee families. They don't endorse us — they don't know us. We just like their work. And if we were in this for the money, we'd sell gold coins and doomsday buckets. We're in it for the bit.",
  },
  {
    n: "05",
    title: "The promise",
    body: "We'll never print something we wouldn't wear, and we'll never ship a joke that isn't sharp enough to deserve the cotton it's printed on. That's the whole deal.",
  },
];

export default function About() {
  useSEO(
    "Our Story — Why We Print Dissent | Fleece The Regime",
    "Why we make funny protest apparel with published fabric specs: humor punches up at the government, garments outlast news cycles, and 5% of annual net profits go to causes such as RAICES.",
    false,
    "/about/"
  );
  return (
    <main data-testid="about-page" className="pt-16">
      <section className="px-6 sm:px-12 lg:px-20 pt-20 pb-16 border-b-2 border-black" data-testid="about-header-band">
        <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-primary mb-6">The Story</p>
        <h1 className="font-display uppercase text-6xl sm:text-7xl lg:text-8xl leading-[0.85] tracking-tighter" data-testid="about-heading">
          Why we're<br />doing <span className="text-primary">this.</span>
        </h1>
        <p className="mt-8 max-w-xl font-mono text-sm sm:text-base leading-relaxed text-black/70">
          Fleece The Regime started with a simple observation: the news is unbearable, the comments section is worse,
          and somehow the most effective response anyone's found is a well-timed joke.
        </p>
      </section>

      <section className="px-6 sm:px-12 lg:px-20 py-8">
        {SECTIONS.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.04}>
            <div
              data-testid={`about-section-${s.n}`}
              className={`flex flex-col sm:flex-row gap-6 sm:gap-14 border-t-2 border-black py-12 ${i % 2 === 1 ? "sm:flex-row-reverse sm:text-right" : ""}`}
            >
              <span className="font-display text-7xl sm:text-8xl leading-none text-primary shrink-0">{s.n}</span>
              <div className={i % 2 === 1 ? "sm:ml-auto" : ""}>
                <h2 className="font-display uppercase text-3xl sm:text-4xl tracking-tight">{s.title}</h2>
                <p className="mt-4 max-w-lg font-mono text-sm leading-relaxed text-black/70">{s.body}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </section>

      <Marquee items={["Aiming at the top, always", "Honest specs only", "Funny beats furious", "Independent by design"]} className="bg-primary text-black" />

      <section className="px-6 sm:px-12 lg:px-20 py-24">
        <Reveal>
          <h2 className="font-display uppercase text-5xl sm:text-6xl leading-[0.85] tracking-tighter" data-testid="about-cta-heading">
            That's the whole story.<br />Now go wear it.
          </h2>
          <Link
            to="/shop"
            data-testid="about-shop-button"
            className="mt-10 inline-flex items-center gap-3 border-2 border-black bg-primary px-10 py-5 font-display uppercase text-2xl text-white shadow-hard btn-press hover:bg-black"
          >
            Shop the drop <ArrowRight size={22} />
          </Link>
        </Reveal>
      </section>
    </main>
  );
}
